import type {
  GeoLocationInfo,
  PalaceCell,
  PanNoteItem,
  PanResult,
  PanSessionSnapshot,
  PanSettings,
  SavedPanRecord,
  SerializedPanResult,
} from './pan-types';

const PAN_RECORDS_KEY = 'qimen_pan_records_v2';
const PAN_RECORDS_LEGACY_KEYS = ['qimen_pan_records_v1'];
const PAN_SETTINGS_KEY = 'qimen_pan_settings_v2';
const PAN_LOCATION_KEY = 'qimen_geo_location_v1';
const PAN_SESSION_KEY = 'qimen_pan_session_v1';
const PAN_SESSION_RESTORE_ONCE_KEY = 'qimen_pan_restore_once_v1';
export const PAN_MODE_ONBOARDING_KEY = 'qimen_mode_onboarding_done_v1';
export const MAX_PAN_RECORDS = 60;
const PAN_SESSION_MAX_AGE_MS = 6 * 60 * 60 * 1000;

const DEFAULT_SETTINGS: PanSettings = {
  defaultMode: 'simple',
  remoteMonthAsSubject: false,
  xiangyiCategoryMode: 'full',
};

const getSafeTimestamp = (raw: unknown, fallback: number): number => {
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? value : fallback;
};

const sortPanNotesByRecent = (notes: PanNoteItem[]): PanNoteItem[] => (
  [...notes].sort((a, b) => (
    Number(b.updatedAt || 0) - Number(a.updatedAt || 0)
    || Number(b.createdAt || 0) - Number(a.createdAt || 0)
    || String(b.id || '').localeCompare(String(a.id || ''), 'zh-CN')
  ))
);

const buildLegacyPanNote = (
  rawNote: unknown,
  fallbackTs: number,
  seed = '',
): PanNoteItem[] => {
  const content = String(rawNote || '').trim();
  if (!content) return [];
  const ts = getSafeTimestamp(fallbackTs, Date.now());
  const suffix = String(seed || '').trim() || Math.random().toString(36).slice(2, 8);
  return [{
    id: `legacy-note-${suffix}`,
    content,
    createdAt: ts,
    updatedAt: ts,
  }];
};

export const normalizePanNotes = (
  rawNotes: unknown,
  legacyNote: unknown,
  fallbackTs: number,
  seed = '',
): PanNoteItem[] => {
  const notes = Array.isArray(rawNotes)
    ? rawNotes
      .map((item, index) => {
        if (!item || typeof item !== 'object') return null;
        const row = item as Record<string, unknown>;
        const content = String(row.content || '').trim();
        if (!content) return null;
        const createdAt = getSafeTimestamp(row.createdAt, fallbackTs);
        const updatedAt = getSafeTimestamp(row.updatedAt, createdAt);
        const id = String(row.id || '').trim() || `note-${seed || 'pan'}-${index}`;
        return {
          id,
          content,
          createdAt,
          updatedAt,
        } as PanNoteItem;
      })
      .filter((item): item is PanNoteItem => Boolean(item))
    : [];
  if (notes.length > 0) return sortPanNotesByRecent(notes);
  return buildLegacyPanNote(legacyNote, fallbackTs, seed);
};

export const getPanRecordNotes = (
  record: Pick<SavedPanRecord, 'id' | 'notes' | 'note' | 'createdAt' | 'updatedAt'> | null | undefined,
): PanNoteItem[] => {
  if (!record) return [];
  const fallbackTs = getSafeTimestamp(record.updatedAt, getSafeTimestamp(record.createdAt, Date.now()));
  return normalizePanNotes(record.notes, record.note, fallbackTs, record.id);
};

export const getPanRecordNoteCount = (
  record: Pick<SavedPanRecord, 'id' | 'notes' | 'note' | 'createdAt' | 'updatedAt'> | null | undefined,
): number => getPanRecordNotes(record).length;

const getLatestPanNoteText = (notes: PanNoteItem[]): string => (
  notes[0]?.content || ''
);

const getRecordUpdatedAt = (record: Pick<SavedPanRecord, 'createdAt' | 'updatedAt'>): number => {
  const updatedAt = Number(record.updatedAt || 0);
  if (Number.isFinite(updatedAt) && updatedAt > 0) return updatedAt;
  const createdAt = Number(record.createdAt || 0);
  return Number.isFinite(createdAt) && createdAt > 0 ? createdAt : 0;
};

export const sortPanRecordsByRecency = (records: SavedPanRecord[]): SavedPanRecord[] => (
  [...records].sort((a, b) => (
    getRecordUpdatedAt(b) - getRecordUpdatedAt(a)
    || Number(b.createdAt || 0) - Number(a.createdAt || 0)
    || String(b.id || '').localeCompare(String(a.id || ''), 'zh-CN')
  ))
);

export const mergePanRecordsById = (records: SavedPanRecord[]): SavedPanRecord[] => {
  const recordMap = new Map<string, SavedPanRecord>();
  records.forEach((record) => {
    const id = String(record?.id || '').trim();
    if (!id) return;
    const existing = recordMap.get(id);
    if (!existing) {
      recordMap.set(id, record);
      return;
    }
    const nextUpdatedAt = getRecordUpdatedAt(record);
    const existingUpdatedAt = getRecordUpdatedAt(existing);
    if (nextUpdatedAt >= existingUpdatedAt) {
      recordMap.set(id, record);
    }
  });
  return sortPanRecordsByRecency(Array.from(recordMap.values()));
};

export const toDateTimeLocalValue = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${d}T${h}:${mm}`;
};

export const parseDateTimeLocal = (value: unknown): Date | null => {
  if (typeof value !== 'string') return null;
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})$/);
  if (!match) return null;

  const [, y, m, d, h, mm] = match;
  const date = new Date(Number(y), Number(m) - 1, Number(d), Number(h), Number(mm), 0, 0);
  if (
    date.getFullYear() !== Number(y)
    || date.getMonth() !== Number(m) - 1
    || date.getDate() !== Number(d)
    || date.getHours() !== Number(h)
    || date.getMinutes() !== Number(mm)
  ) {
    return null;
  }
  return date;
};

export const loadPanSettings = (): PanSettings => {
  try {
    const raw = localStorage.getItem(PAN_SETTINGS_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<PanSettings> | null;
    if (!parsed || (parsed.defaultMode !== 'simple' && parsed.defaultMode !== 'pro')) return DEFAULT_SETTINGS;
    return {
      defaultMode: parsed.defaultMode,
      remoteMonthAsSubject: parsed.remoteMonthAsSubject === true,
      xiangyiCategoryMode: parsed.xiangyiCategoryMode === 'simple' ? 'simple' : 'full',
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
};

export const savePanSettings = (settings: PanSettings): void => {
  localStorage.setItem(PAN_SETTINGS_KEY, JSON.stringify(settings));
};

export const serializePanResult = (pan: PanResult): SerializedPanResult => ({
  ...pan,
  palaces: Array.from(pan.palaces.entries()),
});

export const deserializePanResult = (raw: SavedPanRecord['pan']): PanResult => ({
  ...raw,
  palaces: new Map<number, PalaceCell>(raw.palaces),
});

const normalizeSerializedPalaces = (raw: unknown): Array<[number, PalaceCell]> => {
  if (Array.isArray(raw)) {
    return raw
      .map((entry): [number, PalaceCell] | null => {
        if (!Array.isArray(entry) || entry.length < 2) return null;
        return [Number(entry[0]), entry[1] as PalaceCell];
      })
      .filter((entry): entry is [number, PalaceCell] => (
        Boolean(entry)
        && Number.isFinite(entry[0])
        && entry[0] >= 1
        && entry[0] <= 9
        && entry[1]
        && typeof entry[1] === 'object'
      ))
      .sort((a, b) => a[0] - b[0]);
  }
  if (!raw || typeof raw !== 'object') return [];
  return Object.entries(raw as Record<string, unknown>)
    .map(([positionRaw, cell]) => [Number(positionRaw), cell as PalaceCell] as [number, PalaceCell])
    .filter(([position, cell]) => (
      Number.isFinite(position)
      && position >= 1
      && position <= 9
      && cell
      && typeof cell === 'object'
    ))
    .sort((a, b) => a[0] - b[0]);
};

export const loadPanRecords = (): SavedPanRecord[] => {
  try {
    const candidates = [PAN_RECORDS_KEY, ...PAN_RECORDS_LEGACY_KEYS];
    let sourceKey = '';
    let parsed: unknown[] = [];
    for (const key of candidates) {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const next = JSON.parse(raw);
      if (!Array.isArray(next)) continue;
      sourceKey = key;
      parsed = next;
      break;
    }
    if (!parsed.length) return [];
    const normalized = parsed
      .map((item) => {
        if (!item || typeof item !== 'object') return null;
        const row = item as Record<string, unknown>;
        const id = String(row.id || '').trim();
        if (!id) return null;
        const sourceDateTime = String(row.sourceDateTime || '').trim();
        if (!sourceDateTime) return null;
        const panRaw = row.pan && typeof row.pan === 'object'
          ? row.pan as Record<string, unknown>
          : null;
        if (!panRaw) return null;
        const palaces = normalizeSerializedPalaces(panRaw.palaces);
        if (!palaces.length) return null;
        const createdAtRaw = Number(row.createdAt);
        const createdAt = Number.isFinite(createdAtRaw) && createdAtRaw > 0 ? createdAtRaw : Date.now();
        const updatedAtRaw = Number(row.updatedAt);
        const updatedAt = Number.isFinite(updatedAtRaw) && updatedAtRaw > 0 ? updatedAtRaw : createdAt;
        const notes = normalizePanNotes(row.notes, row.note, updatedAt, id);
        return {
          ...row,
          id,
          question: String(row.question || '').trim() || '未命名事项',
          sourceDateTime,
          createdAt,
          updatedAt,
          consultMode: row.consultMode === 'remote' ? 'remote' : 'face',
          pan: {
            ...panRaw,
            palaces,
          },
          note: getLatestPanNoteText(notes),
          notes,
        } as SavedPanRecord;
      })
      .filter((item): item is SavedPanRecord => Boolean(item));
    const merged = mergePanRecordsById(normalized);
    if (sourceKey && sourceKey !== PAN_RECORDS_KEY && merged.length > 0) {
      savePanRecords(merged);
    }
    return merged;
  } catch {
    return [];
  }
};

export const savePanRecords = (records: SavedPanRecord[]): void => {
  try {
    const normalized = mergePanRecordsById(records)
      .slice(0, MAX_PAN_RECORDS)
      .map((record) => {
        const notes = getPanRecordNotes(record);
        return {
          ...record,
          note: getLatestPanNoteText(notes),
          notes,
        };
      });
    localStorage.setItem(
      PAN_RECORDS_KEY,
      JSON.stringify(normalized),
    );
  } catch {
    // noop
  }
};

export const loadGeoLocation = (): GeoLocationInfo | null => {
  try {
    const raw = localStorage.getItem(PAN_LOCATION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<GeoLocationInfo> | null;
    if (!parsed) return null;
    const lat = Number(parsed.lat);
    const lng = Number(parsed.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return {
      lat,
      lng,
      accuracy: Number.isFinite(Number(parsed.accuracy)) ? Number(parsed.accuracy) : undefined,
      fetchedAt: Number.isFinite(Number(parsed.fetchedAt)) ? Number(parsed.fetchedAt) : Date.now(),
    };
  } catch {
    return null;
  }
};

export const saveGeoLocation = (location: GeoLocationInfo): void => {
  localStorage.setItem(PAN_LOCATION_KEY, JSON.stringify(location));
};

export const loadPanSession = (): PanSessionSnapshot | null => {
  try {
    const raw = sessionStorage.getItem(PAN_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PanSessionSnapshot | null;
    if (!parsed || !parsed.pan || !Array.isArray(parsed.pan.palaces)) return null;
    if (!Number.isFinite(parsed.savedAt)) return null;
    if (Date.now() - parsed.savedAt > PAN_SESSION_MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const savePanSession = (snapshot: PanSessionSnapshot): void => {
  sessionStorage.setItem(PAN_SESSION_KEY, JSON.stringify(snapshot));
};

export const markPanSessionRestoreOnce = (): void => {
  try {
    sessionStorage.setItem(PAN_SESSION_RESTORE_ONCE_KEY, '1');
  } catch {
    // noop
  }
};

export const consumePanSessionRestoreOnce = (): boolean => {
  try {
    const flag = sessionStorage.getItem(PAN_SESSION_RESTORE_ONCE_KEY);
    sessionStorage.removeItem(PAN_SESSION_RESTORE_ONCE_KEY);
    return flag === '1';
  } catch {
    return false;
  }
};

export const clearPanSessionSnapshot = (): void => {
  try {
    sessionStorage.removeItem(PAN_SESSION_KEY);
    sessionStorage.removeItem(PAN_SESSION_RESTORE_ONCE_KEY);
  } catch {
    // noop
  }
};
